/**
 * Mounts inside <LiveKitRoom> (operator OR field side):
 *   - constructs a CommandBus on the room's data channel
 *   - subscribes to inbound annotation/* envelopes via useAnnotationSync
 *   - renders the SVG overlay (editable on operator, read-only on field)
 *   - exposes the bus + identity via context for the toolbar to use
 *   - **also lifts the bus + room into the global `useSessionBus` store**
 *     so SessionPage components OUTSIDE the LiveKitRoom (sidebar
 *     buttons, the End-session action) can publish envelopes too.
 *
 * Children rendered inside the provider so any component the caller
 * passes (toolbar, in-room controls slot, capture handler) gets the
 * bus context via `useAnnotationContext()`.
 *
 * Pulls the initial annotation set from the REST list endpoint on mount —
 * §11 calls this out for late-join recovery; we apply it always so a
 * data-channel hiccup mid-session doesn't silently lose strokes.
 */
import { useRoomContext } from "@livekit/components-react";
import { useQuery } from "@tanstack/react-query";
import { RoomEvent } from "livekit-client";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { useAnnotationStore } from "@/lib/annotation";
import { useAnnotationSync } from "@/lib/annotationSync";
import { makeAuditMirror } from "@/lib/auditMirror";
import {
  CommandBus,
  type EnvelopeIdentity,
  makeLiveKitTransport,
} from "@/lib/commandBus";
import { useEvidenceViewSync } from "@/lib/evidenceView";
import { useLiveDraftsSync } from "@/lib/liveDrafts";
import { useRecordingStore } from "@/lib/recording";
import { useSessionBus } from "@/lib/sessionBus";
import type { Annotation } from "@/lib/types";

import { AnnotationOverlay } from "./AnnotationOverlay";

type Ctx = {
  bus: CommandBus | null;
  self: EnvelopeIdentity;
  sessionId: string;
};

const AnnotationCtx = createContext<Ctx | null>(null);


// eslint-disable-next-line react-refresh/only-export-components -- co-located hook + Provider; splitting just to satisfy HMR ergonomics isn't worth the churn.
export function useAnnotationContext(): Ctx | null {
  return useContext(AnnotationCtx);
}


type Props = {
  sessionId: string;
  /** When true, pointer events draw + publish (operator). */
  editable: boolean;
  /** Field-side only — when present, the audit mirror posts command
   * envelopes via the X-Field-Session-Token path; otherwise it uses the
   * shared Bearer JWT instance (operator). */
  fieldSessionToken?: string;
  /** Children rendered inside the context provider — wrap anything that
   * needs `useAnnotationContext()` here (the toolbar, in-room control
   * slots, capture handlers, etc.). */
  children?: ReactNode;
};


export function InRoomAnnotation({
  sessionId,
  editable,
  fieldSessionToken,
  children,
}: Props) {
  const room = useRoomContext();
  const replaceAll = useAnnotationStore((s) => s.replaceAll);
  const setSessionBus = useSessionBus((s) => s.setBus);
  const [bus, setBus] = useState<CommandBus | null>(null);

  const self: EnvelopeIdentity = useMemo(
    () =>
      ({
        kind: "user",
        id: room?.localParticipant.identity ?? "self",
      }),
    [room],
  );

  // Late-join: fetch the live set once on mount. We also re-fetch
  // on LiveKit reconnect so strokes that happened during a data-
  // channel hiccup get back-filled — otherwise the local store
  // stays stale until the next mount (page reload).
  const annotationsQ = useQuery<Annotation[]>({
    queryKey: ["annotations", sessionId],
    queryFn: async () => {
      const res = await api.get<Annotation[]>(ApiPaths.sessionAnnotations(sessionId));
      replaceAll(res.data);
      return res.data;
    },
  });

  useEffect(() => {
    if (!room) return;
    const onReconnected = () => {
      // The LiveKit data channel is back. Anything that happened
      // during the gap was either undeliverable or replayed via the
      // reliable channel — the REST list is the authoritative state.
      void annotationsQ.refetch();
    };
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room, annotationsQ]);

  useEffect(() => {
    if (!room) return;
    const transport = makeLiveKitTransport(room, sessionId);
    const created = new CommandBus(transport, sessionId, self);
    // Audit mirror: every `command/*` envelope hits the backend audit log
    // so camera/torch commands hash into the per-session chain. Best-effort
    // — see auditMirror.ts for the failure semantics.
    created.setAuditMirror(makeAuditMirror(sessionId, fieldSessionToken));
    setBus(created);
    setSessionBus(created, room);
    return () => {
      created.dispose();
      setBus(null);
      setSessionBus(null, null);
    };
  }, [room, sessionId, self, setSessionBus, fieldSessionToken]);

  useAnnotationSync(bus, sessionId);
  // Mount on both sides so the operator's evidence/view.open envelope
  // surfaces the EvidenceAnnotator on the field's screen too.
  useEvidenceViewSync(bus);
  // Live peer drafts (in-progress strokes broadcast over the unreliable
  // channel as the author drags). Both AnnotationOverlay and
  // EvidenceAnnotator render from the same store, scoped by evidence id.
  useLiveDraftsSync(bus);

  const ctx: Ctx = useMemo(
    () => ({ bus, self, sessionId }),
    [bus, self, sessionId],
  );

  return (
    <AnnotationCtx.Provider value={ctx}>
      <AnnotationOverlay
        sessionId={sessionId}
        editable={editable}
        bus={bus}
        self={self}
      />
      {editable && <PausedNotRecordingBanner />}
      {children}
    </AnnotationCtx.Provider>
  );
}


/** Operator-only banner that surfaces ABOVE the annotation canvas
 *  while recording is paused. Strokes the operator draws during this
 *  window land in the audit chain (and on the live overlay) but are
 *  not burned into video — the merged-recording post-processor
 *  filters out annotations whose entire lifetime fell in a pause gap.
 *  The badge makes that asymmetry visible at draw time.
 *
 *  Z-stack: the overlay SVG is z-20; banner sits at z-30 + non-
 *  interactive so it never swallows pointer events meant for the
 *  canvas underneath. */
function PausedNotRecordingBanner() {
  const { t } = useTranslation();
  const paused = useRecordingStore((s) => s.paused);
  const active = useRecordingStore((s) => s.active);
  if (!(active && paused)) return null;
  return (
    <div className="pointer-events-none absolute left-1/2 top-3 z-30 -translate-x-1/2">
      <div className="flex items-center gap-2 rounded-full bg-amber-500/95 px-3 py-1.5 text-xs font-semibold text-black shadow-lg ring-1 ring-black/20">
        <span className="h-1.5 w-1.5 rounded-full bg-black" aria-hidden />
        {t("annotation.pausedBanner")}
      </div>
    </div>
  );
}